import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

import type { BasicNumberFormatterProps, StyledNumberFormatterProps } from './NumberFormatter.model';

export function useFocusedAndSelectAll(isFocused: boolean, isSelectAll: boolean) {
  const inputRef = useRef<Nullable<HTMLInputElement>>(null);

  useLayoutEffect(() => {
    if (inputRef.current && isFocused) {
      inputRef.current.focus();
      if (isSelectAll) {
        inputRef.current.select();
      }
    }
  }, [isFocused, isSelectAll]);

  return inputRef;
}

export function useIsEditing({
  onBlur,
  onFocus,
  value,
}: Pick<StyledNumberFormatterProps, 'onBlur' | 'onFocus' | 'value'>) {
  const [isEditing, setIsEditing] = useState(false);
  const [valueFrozenDuringEdit, setValueFrozenDuringEdit] = useState<number | null | undefined>(value);
  const handleBlur = useCallback(
    (val: number) => {
      setIsEditing(false);
      setValueFrozenDuringEdit(val);
      if (onBlur) {
        onBlur(val);
      }
    },
    [onBlur],
  );

  const handleFocus = useCallback(() => {
    setIsEditing(true);
    setValueFrozenDuringEdit(value);
    if (onFocus) {
      onFocus();
    }
  }, [onFocus, value]);

  return { handleBlur, handleFocus, isEditing, valueFrozenDuringEdit };
}

export function useFreezeValueDuringEdit({
  onBlur,
  onFocus,
  onIsEditingChange,

  freezeValueDuringEdit,
  value,
  lostInputValue,
}: Pick<
  BasicNumberFormatterProps,
  'onBlur' | 'onFocus' | 'value' | 'onIsEditingChange' | 'lostInputValue' | 'freezeValueDuringEdit'
>) {
  const [isEditing, setIsEditing] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [frozenValue, setFrozenValue] = useState<number | null | undefined>(undefined);

  const handleFocus = useCallback(() => {
    setIsEditing(true);
    setIsDirty(false);
    if (onIsEditingChange) {
      onIsEditingChange(true);
    }

    if (onFocus) {
      onFocus();
    }
  }, [onFocus, onIsEditingChange]);

  const handleBlur = useCallback(
    (v?: number) => {
      setIsEditing(false);
      setIsDirty(false);
      if (onIsEditingChange) {
        onIsEditingChange(false);
      }
      setFrozenValue(undefined);
      if (onBlur) {
        onBlur(v);
      }
    },
    [onBlur, onIsEditingChange],
  );

  const markDirty = useCallback(() => {
    if (!isDirty) {
      setIsDirty(true);
      setFrozenValue(value);
    }
  }, [isDirty, value]);

  const markPristine = useCallback(() => {
    setIsDirty(false);
    setFrozenValue(undefined);
  }, []);

  const valueWithFreeze = (freezeValueDuringEdit ?? true) && isEditing && isDirty ? frozenValue : value;
  const currentValue = lostInputValue ?? valueWithFreeze;
  return { handleBlur, handleFocus, markDirty, isEditing, frozenValue, currentValue, isDirty, markPristine };
}

export type TolerantValueType = number | undefined | null | string;
export type NumericValueType = number | undefined;
export type ApplyValueChange = (oldValue: NumericValueType, newValue: TolerantValueType) => void;

/// This function force update of the component below
export function useApplyValueChange(
  value: TolerantValueType,
  markDirty: () => void,
): [TolerantValueType, ApplyValueChange] {
  const [internalValue, setInternalValue] = useState(value);
  useEffect(() => {
    setInternalValue(value);
  }, [value]);

  const applyValueChange: ApplyValueChange = useCallback(
    (oldValue: NumericValueType, newValue: TolerantValueType) => {
      markDirty();
      setInternalValue(oldValue);
      setTimeout(() => {
        setInternalValue(newValue);
      }, 0);
    },
    [markDirty],
  );

  return [internalValue, applyValueChange];
}
